import { Entity } from '@backstage/catalog-model';

/**
 * Full Azure resource id, per environment:
 *   everdriven.com/azure-resource-prod
 * Mirrored in packages/backend/src/modules/azureMetrics.ts — the backend is
 * what parses them; this side only needs to know whether any is present.
 */
export const RESOURCE_ANNOTATION = 'everdriven.com/azure-resource';
export const RESOURCE_ANNOTATION_PREFIX = 'everdriven.com/azure-resource-';

export interface TimeRange {
  label: string;
  from: string;
  to: string;
}

export const TIME_RANGES: TimeRange[] = [
  { label: 'Last hour', from: 'now-1h', to: 'now' },
  { label: 'Last 6 hours', from: 'now-6h', to: 'now' },
  { label: 'Last 24 hours', from: 'now-24h', to: 'now' },
  { label: 'Last 7 days', from: 'now-7d', to: 'now' },
];

export const DEFAULT_RANGE = TIME_RANGES[1];

export const isAzureMetricsAvailable = (entity: Entity): boolean =>
  Object.keys(entity.metadata.annotations ?? {}).some(
    key =>
      key === RESOURCE_ANNOTATION || key.startsWith(RESOURCE_ANNOTATION_PREFIX),
  );

export interface SeriesPoint {
  t: number;
  v: number | null;
}

export interface Series {
  name: string;
  points: SeriesPoint[];
}

export type MetricUnit = 'percent' | 'bytes' | 'Bps' | 'iops' | 'short';

export interface ChartPayload {
  key: string;
  title: string;
  unit: MetricUnit;
  series: Series[];
}

export interface SeriesResponse {
  resource: { name: string; type: string };
  environment: string;
  environments: string[];
  dashboardUrl: string | null;
  charts: ChartPayload[];
  unavailable: string[];
}

export function seriesUrl(
  baseUrl: string,
  entityRef: string,
  range: TimeRange,
  environment?: string,
): string {
  const params = new URLSearchParams({
    entityRef,
    from: range.from,
    to: range.to,
  });
  // Omitted on the first load so the backend picks the default; sent once the
  // viewer has chosen, so the choice survives a range change.
  if (environment) {
    params.set('environment', environment);
  }
  return `${baseUrl}/series?${params.toString()}`;
}

export interface ChartRow {
  t: number;
  [series: string]: number | null;
}

/**
 * Pivots per-series points into the one-row-per-timestamp shape a chart wants.
 *
 * Series are not assumed to share timestamps: each row carries whichever
 * series have a sample at that instant, and a series missing one is `null`
 * rather than 0 — a gap in the line is honest, a zero is a lie.
 */
export function toChartRows(series: Series[]): ChartRow[] {
  const byTime = new Map<number, ChartRow>();
  for (const s of series) {
    for (const point of s.points) {
      const row = byTime.get(point.t) ?? { t: point.t };
      row[s.name] = point.v;
      byTime.set(point.t, row);
    }
  }
  const rows = [...byTime.values()].sort((a, b) => a.t - b.t);
  for (const row of rows) {
    for (const s of series) {
      if (!(s.name in row)) {
        row[s.name] = null;
      }
    }
  }
  return rows;
}

/** True when every sample of every series is null — an empty chart, not an error. */
export function isEmpty(series: Series[]): boolean {
  return series.every(s => s.points.every(p => p.v === null));
}

/** Nothing came back at all — every chart was empty. */
export function hasNothing(data: SeriesResponse): boolean {
  return data.charts.length === 0;
}

/**
 * Categorical series colors, validated with the dataviz palette checker
 * against each mode's own surface (light on #FFFFFF paper, dark on #12283D).
 * Slot 1 is the brand blue; slot 2 is the stepped orange that clears the
 * lightness band on the dark navy surface — the lighter oranges do not.
 *
 * Fixed order, never cycled: a series keeps its color when another is hidden.
 */
export const SERIES_COLORS = {
  light: ['#1B6EC8', '#EB6834', '#1BAF7A', '#4A3AA7'],
  dark: ['#4C8FD6', '#D95926', '#199E70', '#9085E9'],
} as const;

export function seriesColor(mode: 'light' | 'dark', index: number): string {
  const slots = SERIES_COLORS[mode];
  return slots[index] ?? slots[slots.length - 1];
}

const TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  hour: '2-digit',
  minute: '2-digit',
});
const DATE_TIME_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

/** Axis ticks stay short; ranges over a day need the date to be readable. */
export function formatTick(t: number, range: TimeRange): string {
  const long = range.from === 'now-7d' || range.from === 'now-24h';
  return (long ? DATE_TIME_FORMAT : TIME_FORMAT).format(new Date(t));
}

const BYTE_STEPS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];

function formatBytes(v: number, suffix: string): string {
  const sign = v < 0 ? '-' : '';
  let value = Math.abs(v);
  let step = 0;
  while (value >= 1024 && step < BYTE_STEPS.length - 1) {
    value /= 1024;
    step += 1;
  }
  const digits = value >= 100 || step === 0 ? 0 : 1;
  return `${sign}${value.toFixed(digits)} ${BYTE_STEPS[step]}${suffix}`;
}

function formatShort(v: number): string {
  if (Math.abs(v) >= 1000) {
    return new Intl.NumberFormat(undefined, {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(v);
  }
  return Number.isInteger(v) ? String(v) : v.toFixed(2);
}

/** Formats a value for its unit, matching how Grafana renders the same metric. */
export function formatValue(v: number | null, unit: MetricUnit): string {
  if (v === null || v === undefined) {
    return '—';
  }
  switch (unit) {
    case 'percent':
      return `${Math.abs(v) >= 100 ? v.toFixed(0) : v.toFixed(2)}%`;
    case 'bytes':
      return formatBytes(v, '');
    case 'Bps':
      return formatBytes(v, '/s');
    case 'iops':
      return `${formatShort(v)} IO/s`;
    case 'short':
    default:
      return formatShort(v);
  }
}

/** Axis ticks need to be short; the tooltip carries the precise value. */
export function formatAxisValue(v: number, unit: MetricUnit): string {
  switch (unit) {
    case 'percent':
      return `${Math.round(v)}%`;
    case 'bytes':
      return formatBytes(v, '');
    case 'Bps':
      return formatBytes(v, '/s');
    default:
      return formatShort(v);
  }
}
